<template>
	<div class="pik-widget pik-auth w-full text-12px overflow-hidden">
    <header v-if="!hideBranding" class="flex flex-row justify-center bg-gray-001 p-8 desktop:p-12">
      <img :src="logoUrl" style="width:200px;min-width:200px;max-width:200px;"/>
    </header>
    <div class="p-8 desktop:p-12 bg-white">
      <template v-if="isLoading">
        <section class="w-full h-full flex flex-row justify-center items-center">
          <LoadingSpinner></LoadingSpinner>
        </section>
      </template>
      <template v-else>
        <section class="flex flex-col gap-9">
          <div v-if="!isSingleMode" class="flex flex-row justify-between gap-3 font-medium text-gray-006 text-12px w-full">
            <div class="flex flex-row gap-3">
              <button 
                class="hover:text-red"
                :class="{
                  'text-red underline pointer-events-none': currView === 'login'
                }"
                type="button"
                @click="handleSetCurrView('login')"
              >Login</button>
              <button 
                class="hover:text-red" 
                :class="{
                  'text-red underline pointer-events-none': currView === 'register'
                }"
                type="button"
                @click="handleSetCurrView('register')"
              >Create Account</button>
            </div>
            <button
              v-if="customButton"
              class="hover:text-red self-end"
              type="button"
              @click="handleCustomButtonClick()"
              v-html="customButton"
            ></button>
          </div>
          <div>
            <template v-if="isError">
              <section class="w-full h-full flex flex-row justify-center items-center">
                  <div 
                    class="w-full flex flex-col gap-6 justify-between text-red"
                    :style="{
                      minHeight: minContentHeight 
                    }"
                  >
                    <div class="flex flex-col gap-4">
                      <h3 class="text-24px" v-html="errorTitle"></h3>
                      <p v-html="errorMessage"></p>
                    </div>
                    <div class="flex flex-col gap-3">
                      <div>
                        <div class="flex flex-col gap-3">
                          <button 
                            class="bg-black hover:bg-red text-white rounded-default p-3"
                            type="button"
                            @click="handleTryAgainClick()"
                          >Try Again</button>
                        </div>
                      </div> 
                    </div>
                  </div>
              </section>
            </template>
            <template v-else>
              <div class="relative">
                <div 
                  v-if="currView === 'login'"
                  class="flex flex-col gap-6 justify-between"
                  :style="{
                    minHeight: minContentHeight 
                  }"
                >
                  <div class="flex flex-col gap-4">
                    <h3 class="text-24px text-black">Welcome Back</h3>
                    <p class="text-black">Use your existing email and<br />password to sign in.</p>
                  </div>
                  <form @submit.prevent="handleLoginSubmit()" class="flex flex-col gap-3">
                    <div>
                      <div 
                        v-if="loginView === 'email'"
                        class="flex flex-col gap-0"
                      >
                        <div class="mb-3 flex flex-row justify-between">
                          <div>
                          </div>
                          <div>
                            <button 
                              class="text-gray-006 hover:text-red" 
                              type="button"
                              @click="handleSetCurrView('forgotpassword')"
                            ><p>Forgot password?</p></button>
                          </div>
                        </div>
                        <div class="mb-3">
                          <input 
                            v-model="loginEmail" 
                            ref="loginEmailEl"
                            class="p-3 bg-gray-001 rounded-default border-1 border-solid text-black placeholder-gray-006"
                            :class="{
                              'border-red': loginEmail && !isLoginEmailPayloadValid,
                              'border-gray-003': !loginEmail || isLoginEmailPayloadValid
                            }"
                            type="email" 
                            :placeholder="emailPlaceholder"
                            autocomplete="email"
                            @keyup.enter="handleEmailSubmit()"
                            required
                          >
                        </div>
                        <button 
                          class="bg-black hover:bg-red text-white rounded-default p-3"
                          :class="{
                            'opacity-50 pointer-events-none': !isLoginEmailPayloadValid
                          }"
                          type="button"
                          @click="handleEmailSubmit()"
                        >Continue</button>
                      </div>
                      <div 
                        v-if="loginView === 'password'"
                        class="flex flex-col gap-0"
                      >
                        <div class="mb-3 flex flex-row justify-between">
                          <div>
                            <button 
                              class="text-gray-006 hover:text-red flex flex-row items-center" 
                              type="button"
                              @click="handleSetLoginView('email')"
                            ><Icon 
                              class="text-16px"
                              icon="mdi:chevron-left" 
                            /><p>Back</p></button>
                          </div>
                          <div>
                            <button 
                              class="text-gray-006 hover:text-red" 
                              type="button"
                              @click="handleSetCurrView('forgotpassword')"
                            ><p>Forgot password?</p></button>
                          </div>
                        </div>
                        <div class="relative mb-3">
                          <div class="relative flex flex-row">
                            <input 
                              v-if="!showPassword"
                              v-model="loginPassword"
                              ref="loginPasswordEl"
                              class="text-black p-3 bg-gray-001 rounded-l-default border-1 border-solid border-gray-003 border-r-0 placeholder-gray-006"
                              type="password" 
                              placeholder="Password"
                              autocomplete="current-password"
                              @keyup.enter="handleLoginSubmit()"
                              required
                            >
                            <input 
                              v-else
                              v-model="loginPassword"
                              ref="loginPasswordEl"
                              class="text-black p-3 bg-gray-001 rounded-l-default border-1 border-solid border-gray-003 border-r-0 placeholder-gray-006"
                              type="text" 
                              placeholder="Password"
                              autocomplete="current-password"
                              @keyup.enter="handleLoginSubmit()"
                              required
                            >
                            <div class="pr-3 flex flex-row items-center justify-center bg-gray-001 rounded-r-default border-1 border-solid border-gray-003 border-l-0 placeholder-gray-006">
                              <button
                                v-if="!showPassword"
                                class="text-black"
                                type="button"
                                @click="handleSetShowPassword(true)"
                              ><span>show</span></button>
                              <button 
                                v-else
                                class="text-black"
                                type="button"
                                @click="handleSetShowPassword(false)"
                              ><span>hide</span></button>
                            </div>
                          </div>
                        </div>
                        <button 
                          class="bg-black hover:bg-red text-white rounded-default p-3"
                          :class="{
                            'opacity-50 pointer-events-none': !isLoginPasswordPayloadValid
                          }"
                          type="submit"
                        >Continue</button>
                      </div>
                    </div> 
                    <div class="flex flex-row gap-1 justify-center">
                      <span
                        :class="{
                          'bg-red': loginView === 'email',
                          'bg-gray-004': loginView === 'password'
                        }"
                        style="width:10px; height:3px; border-radius: 1px;"
                      ></span>
                      <span
                        :class="{
                          'bg-red': loginView === 'password',
                          'bg-gray-004': loginView === 'email'
                        }"
                        style="width:10px; height:3px; border-radius: 1px;"
                      ></span>
                    </div>
                  </form>
                </div>
                <div 
                  v-else-if="currView === 'forgotpassword'"
                  class="flex flex-col gap-6 justify-between"
                  :style="{
                    minHeight: minContentHeight 
                  }"
                >
                  <div class="flex flex-col gap-4">
                    <h3 class="text-24px text-black">Forgot Password</h3>
                    <p class="text-black">Enter the email address associated with your account and we will send you an email with a link to reset it.</p>
                  </div>
                  <form @submit.prevent="handlefpSubmit()" class="flex flex-col gap-3">
                    <div>
                      <div class="flex flex-col gap-0">
                        <div class="mb-3">&nbsp;</div>
                        <div class="mb-3">
                          <input 
                            v-model="fpEmail"
                            type="email" 
                            class="text-black p-3 bg-gray-001 rounded-default border-1 border-solid placeholder-gray-006"
                            :class="{
                              'border-red': fpEmail && !isFPEmailValid,
                              'border-gray-003': !fpEmail || isFPEmailValid
                            }"
                            placeholder="Email"
                            required
                          >
                        </div>
                        <button 
                          class="bg-black hover:bg-red text-white rounded-default p-3"
                          :class="{
                            'opacity-50 pointer-events-none': !isFPPayloadValid
                          }"
                          type="submit"
                        >Reset Password</button>
                      </div>
                    </div> 
                  </form>
                </div>
                <div 
                  v-else-if="currView === 'fpsent'"
                  class="flex flex-col gap-6 justify-between"
                  :style="{
                    minHeight: minContentHeight 
                  }"
                >
                  <div class="flex flex-col gap-4">
                    <h3 class="text-24px">Email Sent</h3>
                    <p>{{ fpSuccessMessage }}</p>
                    <p></p>
                  </div>
                  <div class="flex flex-col gap-3">
                    <div>
                      <div class="flex flex-col gap-0">
                        <button 
                          class="bg-black hover:bg-red text-white rounded-default p-3"
                          type="button"
                          @click="handleSetCurrView('login')"
                        >Back to Login</button>
                      </div>
                    </div> 
                  </div>
                </div>
                <div 
                  v-else-if="currView === 'register'"
                  class="flex flex-col gap-6 justify-between"
                  :style="{
                    minHeight: minContentHeight 
                  }"
                >
                  <div class="flex flex-col gap-4">
                    <h3 class="text-24px text-black">Create Account</h3>
                    <p class="text-black">By clicking "Continue", I agree to Kaplan's <a class="text-red hover:underline" href="https://www.kaptest.com/terms-and-condition" target="_blank">terms and conditions</a>, <a class="text-red hover:underline" href="https://kaplan.com/privacy-policy" target="_blank">privacy policy</a> , and <a class="text-red hover:underline" href="https://kaplan.com/privacy-policy" target="_blank">CA Notice of Collection</a>.</p>
                  </div>
                  <form @submit.prevent="handleLoginSubmit()" class="flex flex-col gap-3">
                    <div>
                      <div 
                        v-if="regView === 'required'"
                        class="flex flex-col gap-0"
                      >
                        <div class="mb-3">&nbsp;</div>
                        <div class="flex flex-col gap-3 mb-3">
                          <input 
                            v-model="regRequiredPayload.first_name" 
                            :ref="(el) => setRegRequiredRef(el, 'first_name')"
                            class="text-black p-3 bg-gray-001 rounded-default border-1 border-solid border-gray-003 placeholder-gray-006"
                            type="text" 
                            placeholder="First Name"
                            @keyup.enter="handleRegRequiredSubmit()"
                            required
                          >
                          <input 
                            v-model="regRequiredPayload.last_name" 
                            :ref="(el) => setRegRequiredRef(el, 'last_name')"
                            class="text-black p-3 bg-gray-001 rounded-default border-1 border-solid border-gray-003 placeholder-gray-006"
                            type="text" 
                            placeholder="Last Name"
                            @keyup.enter="handleRegRequiredSubmit()"
                            required
                          >
                          <input 
                            v-model="regRequiredPayload.username" 
                            :ref="(el) => setRegRequiredRef(el, 'username')"
                            class="text-black p-3 bg-gray-001 rounded-default border-1 border-solid placeholder-gray-006"
                            :class="{
                              'border-red': regRequiredPayload.username && !isRegUsernameValid,
                              'border-gray-003': !regRequiredPayload.username || isRegUsernameValid
                            }"
                            type="email" 
                            placeholder="Email"
                            autocomplete="email"
                            @keyup.enter="handleRegUsernameEnter()"
                            required
                          >
                          <div 
                            v-if="!isRegFixedPassword"
                            class="relative flex flex-row"
                          >
                            <input 
                              v-if="!regShowPassword"
                              v-model="regRequiredPayload.password"
                              :ref="(el) => setRegRequiredRef(el, 'password')"
                              class="text-black p-3 bg-gray-001 rounded-l-default border-1 border-solid border-r-0 placeholder-gray-006"
                              :class="{
                                'border-red': regRequiredPayload.password && !isRegPasswordValid,
                                'border-gray-003': !regRequiredPayload.password || isRegPasswordValid
                              }"
                              type="password" 
                              placeholder="Password"
                              autocomplete="new-password"
                              @keyup.enter="handleRegPasswordEnter()"
                              required
                            >
                            <input 
                              v-else
                              v-model="regRequiredPayload.password"
                              :ref="(el) => setRegRequiredRef(el, 'password')"
                              class="text-black p-3 bg-gray-001 rounded-l-default border-1 border-solid border-r-0 placeholder-gray-006"
                              :class="{
                                'border-red': regRequiredPayload.password && !isRegPasswordValid,
                                'border-gray-003': !regRequiredPayload.password || isRegPasswordValid
                              }"
                              type="text" 
                              placeholder="Password"
                              autocomplete="new-password"
                              @keyup.enter="handleRegPasswordEnter()"
                              required
                            >
                            <div 
                              class="text-black pr-3 flex flex-row items-center justify-center bg-gray-001 rounded-r-default border-1 border-solid border-l-0 placeholder-gray-006"
                              :class="{
                                'border-red': regRequiredPayload.password && !isRegPasswordValid,
                                'border-gray-003': !regRequiredPayload.password || isRegPasswordValid
                              }"
                            >
                              <button
                                v-if="!regShowPassword"
                                class="text-black"
                                type="button"
                                @click="handleSetRegShowPassword(true)"
                              ><span>show</span></button>
                              <button 
                                v-else
                                class="text-black"
                                type="button"
                                @click="handleSetRegShowPassword(false)"
                              ><span>hide</span></button>
                            </div>
                          </div>
                          <div 
                            v-if="!isRegFixedPassword"
                            class="relative"
                          >
                            <input 
                              v-if="!regShowPassword"
                              v-model="regConfirmPassword"
                              :ref="(el) => setRegRequiredRef(el, 'confirm_password')"
                              class="text-black p-3 bg-gray-001 rounded-default border-1 border-solid placeholder-gray-006"
                              :class="{
                                'border-red': regConfirmPassword && regConfirmPassword !== regRequiredPayload.password,
                                'border-gray-003': !regConfirmPassword || regConfirmPassword === regRequiredPayload.password
                              }"
                              type="password" 
                              placeholder="Confirm Password"
                              autocomplete="new-password"
                              @keyup.enter="handleRegConfirmPasswordEnter()"
                              required
                            >
                            <input 
                              v-else
                              v-model="regConfirmPassword"
                              :ref="(el) => setRegRequiredRef(el, 'confirm_password')"
                              class="text-black p-3 bg-gray-001 rounded-default border-1 border-solid placeholder-gray-006"
                              :class="{
                                'border-red': regConfirmPassword && regConfirmPassword !== regRequiredPayload.password,
                                'border-gray-003': !regConfirmPassword || regConfirmPassword === regRequiredPayload.password
                              }"
                              type="text" 
                              placeholder="Confirm Password"
                              autocomplete="new-password"
                              @keyup.enter="handleRegConfirmPasswordEnter()"
                              required
                            >
                          </div>
                          <select 
                            v-model="regRequiredPayload.profession_id" 
                            :ref="(el) => setRegRequiredRef(el, 'profession')"
                            class="text-black p-3 bg-gray-001 rounded-default border-1 border-solid border-gray-003 placeholder-gray-006" 
                            required
                          >
                            <option selected value disabled>Choose Occupation</option>
                            <option v-for="profession in professions" :key="profession.id" :value="profession.id">{{ profession.name }}</option>
                          </select>
                          <select 
                            v-model="regRequiredPayload.specialty_id" 
                            :ref="(el) => setRegRequiredRef(el, 'specialty')"
                            class="text-black p-3 bg-gray-001 rounded-default border-1 border-solid border-gray-003 placeholder-gray-006" 
                            required
                          >
                            <option selected value disabled>Choose Specialty</option>
                            <option v-for="specialty in specialties" :key="specialty.id" :value="specialty.id">{{ specialty.name }}</option>
                          </select>
                        </div>
                        <button 
                          class="bg-black hover:bg-red text-white rounded-default p-3"
                          :class="{
                            'opacity-50 pointer-events-none': !isRegRequiredPayloadValid
                          }"
                          type="button"
                          @click="handleRegRequiredSubmit()"
                        >Continue</button>
                      </div>
                      <div 
                        v-if="regView === 'optional'"
                        class="flex flex-col gap-0"
                      >
                        <div class="mb-3">&nbsp;</div>
                        <div class="flex flex-col gap-3 mb-3">
                          <div class="relative flex flex-row">
                            <div class="text-black p-3 bg-gray-001 rounded-l-default border-1 border-solid border-gray-003 text-black placeholder-gray-006">DOB</div>
                            <input 
                              v-model="regOptionalPayload.date_of_birth" 
                              :ref="(el) => setRegOptionalRef(el, 'date_of_birth')"
                              class="text-black p-3 bg-gray-001 rounded-r-default border-1 border-solid border-gray-003 placeholder-gray-006" 
                              type="date" 
                              placeholder="Date of Birth"
                              @keyup.enter="handleRegOptionalSubmit()"
                              required
                            >
                          </div>
                          <select 
                            v-model="regOptionalPayload.country" 
                            :ref="(el) => setRegOptionalRef(el, 'country')"
                            class="text-black p-3 bg-gray-001 rounded-default border-1 border-solid border-gray-003 placeholder-gray-006" 
                            required
                          >
                            <option selected value disabled>Choose Country</option>
                            <option v-for="country in countries" :key="country.order" :value="country.name">{{ country.name }}</option>
                          </select>
                          <input 
                            v-model="regOptionalPayload.zipcode" 
                            :ref="(el) => setRegOptionalRef(el, 'zipcode')"
                            class="text-black p-3 bg-gray-001 rounded-default border-1 border-solid border-gray-003 placeholder-gray-006" 
                            type="text" 
                            placeholder="Zipcode"
                            @keyup.enter="handleRegOptionalSubmit()"
                            required
                          >
                          <input 
                            v-model="regOptionalPayload.npi_number" 
                            :ref="(el) => setRegOptionalRef(el, 'npi_number')"
                            class="text-black p-3 bg-gray-001 rounded-default border-1 border-solid border-gray-003 placeholder-gray-006" 
                            type="text" 
                            placeholder="NPI Number"
                            @keyup.enter="handleRegOptionalSubmit()"
                            required
                          >
                        </div>
                        <div class="flex flex-col gap-3">
                          <button 
                            class="bg-black hover:bg-red text-white rounded-default p-3"
                            :class="{
                              'opacity-50 pointer-events-none': !isRegOptionalPayloadValid
                            }"
                            type="button"
                            @click="handleRegOptionalSubmit()"
                          >Continue</button>
                          <button 
                            class="bg-red hover:bg-black text-white rounded-default p-3"
                            type="button"
                            @click="handleRegSkipOptional()"
                          >Skip</button>
                        </div>
                      </div>
                    </div>
                    <div v-if="!regSkipOptional" class="flex flex-row gap-1 justify-center">
                      <span
                        :class="{
                          'bg-red': regView === 'required',
                          'bg-gray-004': regView === 'optional'
                        }"
                        style="width:10px; height:3px; border-radius: 1px;"
                      ></span>
                      <span
                        :class="{
                          'bg-red': regView === 'optional',
                          'bg-gray-004': regView === 'required'
                        }"
                        style="width:10px; height:3px; border-radius: 1px;"
                      ></span>
                    </div>
                  </form>
                </div>
                <div 
                  v-else-if="currView === 'regSuccess'"
                  class="flex flex-col gap-6 justify-between"
                  :style="{
                    minHeight: minContentHeight 
                  }"
                >
                  <div class="flex flex-col gap-4">
                    <h3 class="text-24px" v-html="regSuccessTitle"></h3>
                    <p v-html="regSuccessMessage"></p>
                    <p></p>
                  </div>
                  <div class="flex flex-col gap-3">
                    <div>
                      <div class="flex flex-col gap-0">
                        <button 
                          class="bg-black hover:bg-red text-white rounded-default p-3"
                          type="button"
                          @click="handleRegSuccessContinueClick()"
                        >Continue</button>
                      </div>
                    </div> 
                  </div>
                </div>
              </div>
            </template>
          </div>
        </section>
      </template>
    </div>
	</div>
</template>

<script setup>
import { Icon } from '@iconify/vue'
import { ref, onMounted, computed, watch, nextTick, defineEmits, defineProps } from 'vue'
import store from '@/store'
import { 
  LoadingSpinner
} from '@/components/'

const emit = defineEmits([
  'auth-init',
  'view-change',
  'login-success',
  'login-error',
  'password-reset-success',
  'password-reset-error',
  'user-created',
  'registration-success',
  'registration-error',
  'registration-skip-optional-info',
  'registration-optional-info-success',
  'registration-optional-info-error',
  'custom-button-click'
])

const props = defineProps({
  isSingleMode: {
    type: [Boolean, String],
    default: false
  },
  logoUrl: {
    type: String,
    default: 'https://cdn.atpoc.com/assets/production/pik/logo/pik-poc-kaplan-logo.png'
  },
  hideBranding: {
    type: [Boolean, String],
    default: false
  },
  defaultMode: {
    type: String,
    default: 'login'
  },
  loginDefaultPayload: {
    type: [String, null],
    default: null
  },
  disableRegister: {
    type: [Boolean, String],
    default: false
  },
  regSkipOptional: {
    type: [Boolean, String],
    default: false
  },
  regDefaultPayload: {
    type: [String, null],
    default: null
  },
  isRegFixedPassword: {
    type: [Boolean, String],
    default: false
  },
  showRegSuccess: {
    type: [Boolean, String],
    default: false
  },
  specialtyList: {
    type: [String, null],
    default: null
  },
  professionList: {
    type: [String, null],
    default: null
  },
  countryList: {
    type: [String, null],
    default: null
  },
  customButton: {
    type: [String, null],
    default: null
  }
})

// Variables
const isLoading = ref(true)
const isError = ref(false)
const currView = ref(props.defaultMode)
const refErrorTitle = ref(null)
const refErrorMessage = ref(null)
const refErrorRedirect = ref(null)
const defaultErrorTitle = ref('Error')
const defaultErrorMessage = ref('An error occurred while processing your request. Please try again later or contact support if the issue persists.')
const loginView = ref('email')
const minContentHeight = '240px'
const showPassword = ref(false)
const loginEmail = ref(null)
const loginPassword = ref(null)
const fpEmail = ref(null)
const emailPlaceholder = ref('Email') 
const loginEmailEl = ref(null)
const loginPasswordEl = ref(null)
const fpSuccessMessage = ref('If an account exists for the provided email, a password reset link has been sent. Please check your inbox (and spam folder) for further instructions.')
const referenceData = ref(null)

const regView = ref('required')
const regRequiredPayload = ref({
  first_name: null,
  last_name: null,
  username: null,
  password: null,
  profession_id: '',
  specialty_id: ''
})
const regConfirmPassword = ref(null)
const regRequiredPayloadEls = ref({})
const regOptionalPayload = ref({
  date_of_birth: null,
  country: '',
  zipcode: null,
  npi_number: null
})
const regOptionalPayloadEls = ref({})
const regShowPassword = ref(false)
const regToken = ref('87157d86c2ccafb4a308897a315d62a0')
const regSuccessTitle = ref('Thank you')
const regSuccessMessage = ref('Your account has been created successfully.')

// Computed
const errorTitle = computed(() => refErrorTitle.value || defaultErrorTitle.value)
const errorMessage = computed(() => refErrorMessage.value || defaultErrorMessage.value)
const isLoginEmailValid = computed(() => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(loginEmail.value))
const isFPEmailValid = computed(() => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(fpEmail.value))
const isRegUsernameValid = computed(() => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(regRequiredPayload.value.username))
const isRegPasswordValid = computed(() => /^(?!.*\s)(?=.*[A-Za-z!@#$%^&*_+])(?=.*\d).{8,}$/.test(regRequiredPayload.value.password))
const professions = computed(() => {
  if (props.professionList && JSON.parse(props.professionList)?.length > 0) {
    const professionList = JSON.parse(props.professionList)
    return professionList?.slice().sort((a, b) => parseInt(a.order) - parseInt(b.order))
  }
  return referenceData.value?.professions?.slice().sort((a, b) => parseInt(a.order) - parseInt(b.order)) || []
})
const specialties = computed(() => {
  if (props.specialtyList && JSON.parse(props.specialtyList)?.length > 0) {
    const specialtyList = JSON.parse(props.specialtyList)
    return specialtyList?.slice().sort((a, b) => parseInt(a.order) - parseInt(b.order))
  }
  return referenceData.value?.specialties?.slice().sort((a, b) => parseInt(a.order) - parseInt(b.order)) || []
})
const countries = computed(() => {
  if (props.countryList && JSON.parse(props.countryList)?.length > 0) {
    const countryList = JSON.parse(props.countryList)
    return countryList?.slice().sort((a, b) => parseInt(a.order) - parseInt(b.order))
  }
  return referenceData.value?.countries?.slice().sort((a, b) => parseInt(a.order) - parseInt(b.order)) || []
})

const isLoginEmailPayloadValid = computed(() => loginEmail.value != null && loginEmail.value !== '' && isLoginEmailValid.value)
const isLoginPasswordPayloadValid = computed(() => loginPassword.value)
const isFPPayloadValid = computed(() => fpEmail.value && isFPEmailValid.value)
const isRegPasswordMatch = computed(() => regRequiredPayload.value.password === regConfirmPassword.value)
const isRegRequiredPayloadValid = computed(() => Object.values(regRequiredPayload.value).every(value => value != null && value !== '') && regConfirmPassword.value && isRegUsernameValid.value)
const isRegOptionalPayloadValid = computed(() => Object.values(regOptionalPayload.value).some(value => value != null && value !== ''))

// Event Handler
const handleSetCurrView = (val) => {
  currView.value = val
  handleResetValues()
  emit('view-change', val)
}

const handleSetLoginView = async (val) => {
  loginView.value = val
}

const handleSetShowPassword = (val) => {
  showPassword.value = val
}

const handleEmailSubmit = async () => {
  if (currView.value !== 'login' || loginView.value !== 'email') return

  if (loginEmail.value && isLoginEmailValid.value) {
    handleSetLoginView('password')
    await nextTick()
    loginPasswordEl.value?.focus()
  }
  else {
    const el = loginEmailEl.value
    if (!el || !el.checkValidity()) {
      el?.reportValidity()
      el?.focus()
      return
    }
  }
}

const handleLoginSubmit = async () => {
  if (loginPassword.value) {
    isLoading.value = true
    await submitUserLogin({
      email: loginEmail.value,
      password: loginPassword.value
    })
    isLoading.value = false
  }
}

const handlefpSubmit = async () => {
  if (fpEmail.value) {
    isLoading.value = true
    await submitResetPassword({
      email: fpEmail.value
    })
    isLoading.value = false
  }
}

const handleResetValues = () => {
  refErrorRedirect.value = null
  handleSetLoginView('email')
  handleSetRegView('required')
  loginEmail.value = null
  loginPassword.value = null
  fpEmail.value = null
  regRequiredPayload.value = {
    first_name: null,
    last_name: null,
    username: null,
    password: null,
    profession_id: '',
    specialty_id: ''
  }
  regConfirmPassword.value = null
  regOptionalPayload.value = {
    date_of_birth: null,
    country: '',
    zipcode: null,
    npi_number: null
  }
  refErrorTitle.value = null
  refErrorMessage.value = null
  isError.value = false
}

const handleTryAgainClick = () => {
  currView.value = refErrorRedirect.value || 'login'
  handleResetValues()
}

const handleCustomButtonClick = () => {
  emit('custom-button-click')
}

const handleSetRegView = (val) => {
  regView.value = val
}

const handleRegUsernameEnter = () => {
  if (!isRegUsernameValid.value) {
    const el = regRequiredPayloadEls.value['username']
    if (el || !el.checkValidity()) {
      el.reportValidity()
      el.focus()
      return
    }
  }

  handleRegRequiredSubmit()
}

const handleRegPasswordEnter = () => {
  if (!isRegPasswordValid.value) {
    const el = regRequiredPayloadEls.value['password']
    if (el) {
      el.setCustomValidity('Password must be at least 8 characters with no spaces, containing both letters or special characters (!@#$%^&+) and at least one number.')
      el.reportValidity()
      el.focus()
      return
    }
  }

  handleRegRequiredSubmit()
}

const handleRegConfirmPasswordEnter = () => {
  if (regRequiredPayload.value.password !== regConfirmPassword.value) {
    const el = regRequiredPayloadEls.value['confirm_password']
    if (el || !el.checkValidity()) {
      el.setCustomValidity('Passwords do not match')
      el.reportValidity()
      el.focus()
      return
    }
  }

  handleRegRequiredSubmit()
}

const handleRegRequiredSubmit = async () => {
  // Validate password match
  if (!isRegPasswordMatch.value) {
    const el = regRequiredPayloadEls.value['confirm_password']
    if (el) {
      el.setCustomValidity('Passwords do not match')
      el.reportValidity()
      el.focus()
      return
    }
  }

  // Validate empty fields first
  const emptyField = Object.keys(regRequiredPayload.value)
    .find((key) => {
      const value = regRequiredPayload.value[key]
      return value === null || value === ''
    })

  if (emptyField) {
    const el = regRequiredPayloadEls.value[emptyField]
    if (el) {
      el.reportValidity()
      el.focus()
      return
    }
  }

  // Validate email format
  if (!isRegUsernameValid.value) {
    const el = regRequiredPayloadEls.value['username']
    if (el) {
      el.setCustomValidity('Please enter a valid email address')
      el.reportValidity()
      el.focus()
      return
    }
  }

  // Validate password pattern
  if (!isRegPasswordValid.value) {
    const el = regRequiredPayloadEls.value['password']
    if (el) {
      el.setCustomValidity('Password must be at least 8 characters with no spaces, containing both letters or special characters (!@#$%^&+) and at least one number.')
      el.reportValidity()
      el.focus()
      return
    }
  }

  isLoading.value = true
  await submitCreateUser(regRequiredPayload.value)
  isLoading.value = false
}

const handleRegOptionalSubmit = async () => {
  isLoading.value = true
  await submitUpdateUser(regOptionalPayload.value)
  isLoading.value = false
}

const handleSetRegShowPassword = (val) => {
  regShowPassword.value = val
}

const handleRegSkipOptional = () => {
  isLoading.value = true
  emit('registration-skip-optional-info')
  if (props.showRegSuccess) {  
    handleSetCurrView('regSuccess')
  }
  else {
    emit('registration-success')
    handleSetCurrView('register')
  }
  handleResetValues()
  isLoading.value = false
}

const handleRegSuccessContinueClick = () => {
  emit('registration-success')
  handleSetCurrView('register')
}

// Functions
const setRegRequiredRef = (el, key) => {
  regRequiredPayloadEls.value[key] = el
}

const setRegOptionalRef = (el, key) => {
  regOptionalPayloadEls.value[key] = el
}

const submitUserLogin = async (payload) => {
  const url = process.env.VUE_APP_AUTH_API_URL
  try {
    const getToken = await store.dispatch('CRUD', {
      method: 'GET',
      url: `${ url }?email=${ payload.email }&password=${ payload.password }`
    })

    if (getToken.error) throw (getToken.error)
    else {
      emit('login-success', getToken.token)
      handleSetCurrView('login')
    }
  }
  catch (error) {
    emit('login-error')
    handleSetCurrView(null)
    refErrorTitle.value = 'Log In Unsuccessful'
    refErrorMessage.value = error
    isError.value = true
  }
}

const submitResetPassword = async (payload) => {
  const url = process.env.VUE_APP_FORGOT_PASSWORD_API_URL
  try {
    const getToken = await store.dispatch('CRUD', {
      method: 'GET',
      url: `${ url }?email=${ payload.email }&password=${ payload.password }`
    })

    if (getToken.error) throw (getToken.error)
    else {
      emit('password-reset-success', payload.email)
      handleSetCurrView('fpsent')
    }
  }
  catch (error) {
    emit('password-reset-error')
    handleSetCurrView(null)
    refErrorTitle.value = 'Password Reset Unsuccessful'
    refErrorMessage.value = error
    refErrorRedirect.value = 'login'
    isError.value = true
  }
}

const getReferenceData = async () => {
  const url = process.env.VUE_APP_REFERENCE_DATA_API_URL
  const res = await fetch(url)
  const data = await res.json()
  
  referenceData.value = data
}

const submitCreateUser = async (payload) => {
  const url = process.env.VUE_APP_REG_API_URL
  try {
    const createUser = await store.dispatch('CRUD', {
      method: 'POST',
      url,
      payload
    })

    if  (!createUser.token) throw (createUser.token)
    else {
      regToken.value = createUser.token
      emit('user-created', createUser.token)
      if (props.regSkipOptional && props.showRegSuccess) handleSetCurrView('regSuccess')
      else if (props.regSkipOptional && !props.showRegSuccess) emit('registration-success', createUser.token)
      else if (!props.regSkipOptional) handleSetRegView('optional')
    }
  }
  catch (error) {
    emit('registration-error')
    handleSetCurrView(null)
    refErrorTitle.value = 'Error'
    refErrorMessage.value = error
    refErrorRedirect.value = 'register'
    isError.value = true
  }
}

const submitUpdateUser = async (payload) => {
  const url = process.env.VUE_APP_PROFILE_API_URL
  try {
    const updateUser = await store.dispatch('CRUD', {
      method: 'PUT',
      url: `${ url }?token=${ regToken.value }`,
      payload
    })

    if (!updateUser) throw (updateUser)
    else {
      emit('registration-optional-info-success')
      handleResetValues()
      if (props.showRegSuccess) {
        handleSetCurrView('regSuccess')
      }
      else {
        handleSetCurrView('register')
        emit('registration-success')
      }
    }
  }
  catch (error) {
    emit('registration-optional-info-error')
    handleSetCurrView(null)
    refErrorTitle.value = 'Error'
    refErrorMessage.value = error
    refErrorRedirect.value = 'login'
    isError.value = true
  }
}

// Watchers
watch(
  () => props.loginDefaultPayload,
  async (newPayload) => {
    const payload = newPayload ? JSON.parse(newPayload) : null
    if (payload?.email) {
      loginEmail.value = payload?.email
      if (payload?.password) {
        loginPassword.value = payload?.password
        await nextTick()
        handleLoginSubmit()
      }
    }
  },
  { immediate: true }
)

watch(
  () => props.regDefaultPayload,
  async (newPayload) => {
    if (newPayload) {
      const payload = JSON.parse(newPayload)
      regRequiredPayload.value = Object.assign(regRequiredPayload.value, payload)
      if (payload.password) {
        regConfirmPassword.value = payload.password
      }
    }
  },
  { immediate: true }
)

watch(
  () => regRequiredPayload.value,
  (newPayload) => {
    console.log('update required payload: ', newPayload)
  },
  { immediate: true, deep: true }
)

// Life Cycle Hooks
onMounted(() => {
  isLoading.value = false
  getReferenceData()
  emit('auth-init')
})
</script>

<style src="@/assets/css/tailwind.css"></style>
<style src="@/assets/css/_fontface.css"></style>
<style src="@/assets/css/_variables.css"></style>
<style src="@/assets/css/_global.css"></style>
<style src="@/assets/css/_animations.css"></style>
<style src="@/assets/css/main.css"></style>